// Refresh source loader + new_answers builder for product refresh (KAN-1464).
//
// Extracted from UpdateProductModal.tsx's load path:
// - Project name/description: raw fetchWithAuth(`/api/project/${productId}`) GET
//   (UpdateProductModal.tsx:406-427). There is no api.ts wrapper for this exact
//   endpoint (fetchProjectByIdAPI hits the different `/api/user/project/{id}`
//   route), so the loader replicates the modal's raw call via fetchWithAuth per
//   CLAUDE.md's fetchWithAuth-only convention.
// - Answers + answerIds mapping: getUserAnswersWithQuestions(projectid, token)
//   (api.ts:537), decoded the same way as UpdateProductModal.tsx:437-465 —
//   switch on answer.questionid for TYPE(20)/USERS(22)/PROBLEM(23)/SOLUTION(24)/NFR(27)/EDGE_CASES(28)/LATENCY(29),
//   and separately capture questionid 25 (team) for the users/team string[]
//   decode (the modal only rolls Q25 into its rollback `origAnswers` list; it
//   never splits users/team into arrays because its own form field is a single
//   free-text string). RefreshSource.users/team split on "\n" to match the
//   newProduct module's string[] convention (newProduct/state.ts) since
//   RefreshSource feeds that reducer's hydrate path (Task 5).
// - Product files list: the modal only fetches
//   `/api/projects/{productId}/vector-files` (UpdateProductModal.tsx:474-478)
//   to get a *count* (`filesData.relations?.length`) — that endpoint's rows
//   are `{vector_files_project_cr_id, projectid, fileid, history}` with no
//   filename (see ai.vector_files_project_cr / VectorFilesProjectCR.to_dict()
//   in the backend). To satisfy RefreshSource.files (which Task 8's
//   FilesModal needs a display name for), the loader resolves names via the
//   existing getUserVectorFiles(idToken, userid) wrapper (api.ts:733-742,
//   GET /api/vector_files/get_all_user_files/<user_id>), matching the Files
//   V6 pattern in FileViewer.tsx, then filters client-side down to the
//   project's fileids. NOTE: the per-file GET /api/vector_files/get?fileid=X
//   route is dead server-side — it calls VectorFilesService().get_vector_file,
//   a method that only exists on FileService, so every call 500s. Do not use
//   it. Any project fileid absent from the user's own files list (e.g.
//   uploaded by a teammate rather than the current user) still gets included
//   in RefreshSource.files with a fallback name `File ${fileid}` rather than
//   being silently dropped.
//
// buildNewAnswers preserves the modal's fixed Q20/22/23/24 construction and
// adds the eight KAN-1729 NFR questions resolved by stable variable. It does
// not include team/Q25, matching the existing quick-compare contract.

import { getAllQuestions, getUserAnswersWithQuestions, getUserVectorFiles } from "@/api/api";
import { fetchIntegrationLinks, type IntegrationProviderId, type VendorConnectionDto } from "@/api/integrations/links";
import fetchWithAuth from "@/api/secureFetch";
import { emptyNfrValues, NFR_FIELDS, type NfrKey, type SourceConnection, type SourceId } from "../newProduct/state";

import type { RefreshAnswerIds, RefreshCoreValues, RefreshNfrQuestionIds } from "./types";

const BACKEND_URI = import.meta.env.VITE_API_BASE_URL || "";

// Question IDs for workproduct_user_answers mapping (UpdateProductModal.tsx:128-133).
const QUESTION_IDS = {
    TYPE: 20,
    USERS: 22,
    PROBLEM: 23,
    SOLUTION: 24,
    TEAM: 25
} as const;

export interface RefreshSource {
    projectId: number;
    core: RefreshCoreValues; // current values, decoded from project row + answers Q20/22/23/24
    users: string[]; // decoded from Q22 the same way the modal splits them
    team: string[]; // decoded from Q25 if present, else []
    answerIds: RefreshAnswerIds;
    nfrQuestionIds: RefreshNfrQuestionIds;
    originalAnswers: unknown[]; // raw rows for quick-compare payload
    originalDescription: string;
    files: Array<{ fileId: number; fileName: string }>;
    connections: Partial<Record<SourceId, SourceConnection>>;
}

const SOURCE_BY_PROVIDER: Record<IntegrationProviderId, Exclude<SourceId, "files">> = {
    jira: "jira",
    "azure-devops": "azure",
    github: "github",
    gitlab: "gitlab",
    bitbucket: "bitbucket",
    "azure-repos": "azuregit"
};

async function fetchProductConnections(token: string, projectId: number): Promise<Partial<Record<SourceId, SourceConnection>>> {
    let vendors: VendorConnectionDto[];
    try {
        ({ vendors } = await fetchIntegrationLinks(token));
    } catch (error) {
        // Integration availability must not regress the existing text/file
        // refresh path. Chips remain connectable and will retry through their
        // provider driver when opened.
        console.error("Failed to load product integration links", error);
        return {};
    }
    const connections: Partial<Record<SourceId, SourceConnection>> = {};

    for (const vendor of vendors) {
        const links = vendor.links.filter(link => link.projectId === projectId);
        if (links.length === 0) continue;
        const source = SOURCE_BY_PROVIDER[vendor.provider];
        const isGit = ["github", "gitlab", "bitbucket", "azure-repos"].includes(vendor.provider);
        connections[source] = {
            connected: true,
            account: vendor.siteOrOrg || vendor.organization || vendor.provider,
            selection: links.map(link => ({
                // Git target pickers key rows by clone URL; tracker pickers key
                // by provider project id. Preserve those same identifiers when
                // hydrating an existing product so current selections match.
                id: String(isGit ? link.externalRef : (link.metadata?.id ?? link.externalRef)),
                key: String(link.metadata?.key ?? link.metadata?.branch ?? "") || undefined,
                name: link.externalName
            })),
            existingLinkIds: links.map(link => link.id),
            syncedCount: links.reduce((count, link) => count + (link.syncedCount ?? 0), 0)
        };
    }

    return connections;
}

// Tiered decode: newline is the primary convention going forward (the new
// refresh screen's ListEditorCard rows are joined with "\n" on write), but
// legacy answers written through the old UpdateProductModal came from a
// single-line (non-multiline) TextField and so NEVER contain newlines —
// they're free text like "Admins, Editors, Viewers". When the newline split
// collapses to a single entry, fall back to splitting on ";" then "," so
// those legacy values still decode into multiple chips instead of one.
function splitList(value: string): string[] {
    const byNewline = value
        .split(/\r\n|\n/)
        .map(line => line.trim())
        .filter(line => line.length > 0);

    if (byNewline.length !== 1) {
        return byNewline;
    }

    for (const delimiter of [";", ","]) {
        const parts = byNewline[0]
            .split(delimiter)
            .map(part => part.trim())
            .filter(part => part.length > 0);
        if (parts.length > 1) {
            return parts;
        }
    }

    return byNewline;
}

async function fetchProjectDetails(token: string, projectId: number): Promise<{ name: string; description: string }> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/project/${projectId}`, token, { method: "GET" });
    if (!response.ok) {
        throw new Error(`Fetching project failed: ${response.statusText}`);
    }
    const project = await response.json();
    return {
        name: project?.project_name || "",
        description: project?.project_description || ""
    };
}

async function fetchProjectFiles(token: string, projectId: number, userId: number): Promise<Array<{ fileId: number; fileName: string }>> {
    const relationsResponse = await fetchWithAuth(`${BACKEND_URI}/api/projects/${projectId}/vector-files`, token, {
        method: "GET"
    });
    if (!relationsResponse.ok) {
        throw new Error(`Fetching project files failed: ${relationsResponse.statusText}`);
    }
    const relationsData = await relationsResponse.json();
    const fileIds: number[] = (relationsData?.relations || []).map((relation: { fileid: number }) => relation.fileid);

    if (fileIds.length === 0) {
        return [];
    }

    // Resolve filenames with a single call to the user's full vector-files
    // list, then filter client-side to this project's fileids. The
    // per-file GET /api/vector_files/get?fileid=X route is dead server-side
    // (see module comment above) so it must not be used here.
    const userFiles = await getUserVectorFiles(token, userId);
    const filenameByFileId = new Map(userFiles.map(file => [file.fileid, file.filename]));

    return fileIds.map(fileId => ({
        fileId,
        // A project fileid may be absent from the current user's own files
        // list when a teammate uploaded it — include it with a fallback
        // name instead of dropping it from RefreshSource.files.
        fileName: filenameByFileId.get(fileId) ?? `File ${fileId}`
    }));
}

/**
 * Load the current state of a product for refresh mode: project name/
 * description, the four core Q&A fields (type/users/problem/solution),
 * users/team decoded into string[] lists, the answer ids needed to write
 * back changes, the raw answer rows (for the quick-compare payload), and
 * the product's linked files.
 *
 * `userId` is required to resolve file names via getUserVectorFiles (see
 * module comment) — the caller must pass the current user's id.
 */
export async function loadRefreshSource(token: string, projectId: number, userId: number): Promise<RefreshSource> {
    const [projectDetails, answers, questions, files, connections] = await Promise.all([
        fetchProjectDetails(token, projectId),
        getUserAnswersWithQuestions(projectId, token),
        getAllQuestions(token),
        fetchProjectFiles(token, projectId, userId),
        fetchProductConnections(token, projectId)
    ]);

    const questionsByVariable = new Map(questions.map(question => [question.variable, question.questionid]));
    const missingNfrVariables = NFR_FIELDS.map(field => field.variable).filter(variable => !questionsByVariable.has(variable));
    if (missingNfrVariables.length > 0) {
        throw new Error(`NFR configuration is incomplete: ${missingNfrVariables.join(", ")}`);
    }
    const nfrQuestionIds = Object.fromEntries(NFR_FIELDS.map(field => [field.key, questionsByVariable.get(field.variable)!])) as RefreshNfrQuestionIds;
    const nfrKeyByQuestionId = new Map<number, NfrKey>(NFR_FIELDS.map(field => [nfrQuestionIds[field.key], field.key]));

    const answerIds: RefreshAnswerIds = {
        type: null,
        users: null,
        problem: null,
        solution: null,
        team: null,
        nfr: Object.fromEntries(NFR_FIELDS.map(field => [field.key, null])) as Record<NfrKey, number | null>
    };

    const core: RefreshCoreValues = {
        name: projectDetails.name,
        description: projectDetails.description,
        type: "",
        users: "",
        problem: "",
        solution: "",
        team: "",
        nfr: emptyNfrValues()
    };

    for (const answer of answers as any[]) {
        const nfrKey = nfrKeyByQuestionId.get(answer.questionid);
        if (nfrKey !== undefined) {
            core.nfr[nfrKey] = answer.answer || "";
            answerIds.nfr[nfrKey] = answer.answer_id;
            continue;
        }
        switch (answer.questionid) {
            case QUESTION_IDS.TYPE:
                core.type = answer.answer || "";
                answerIds.type = answer.answer_id;
                break;
            case QUESTION_IDS.USERS:
                core.users = answer.answer || "";
                answerIds.users = answer.answer_id;
                break;
            case QUESTION_IDS.PROBLEM:
                core.problem = answer.answer || "";
                answerIds.problem = answer.answer_id;
                break;
            case QUESTION_IDS.SOLUTION:
                core.solution = answer.answer || "";
                answerIds.solution = answer.answer_id;
                break;
            case QUESTION_IDS.TEAM:
                core.team = answer.answer || "";
                answerIds.team = answer.answer_id;
                break;
            default:
                break;
        }
    }

    return {
        projectId,
        core,
        users: splitList(core.users),
        team: splitList(core.team),
        answerIds,
        nfrQuestionIds,
        originalAnswers: answers,
        originalDescription: projectDetails.description,
        files,
        connections
    };
}

/**
 * Build the new_answers payload for quick-compare. Extends the modal's fixed
 * Q20/22/23/24 construction with all eight KAN-1729 NFR fields. NFR question
 * IDs come from the live question catalog because they differ by environment.
 * Team/Q25 remains outside quick compare, preserving the existing contract.
 */
export function buildNewAnswers(originalAnswers: unknown[], nfrQuestionIds: RefreshNfrQuestionIds, edited: RefreshCoreValues): unknown[] {
    void originalAnswers;
    return [
        { questionid: QUESTION_IDS.TYPE, answer: edited.type },
        { questionid: QUESTION_IDS.USERS, answer: edited.users },
        { questionid: QUESTION_IDS.PROBLEM, answer: edited.problem },
        { questionid: QUESTION_IDS.SOLUTION, answer: edited.solution },
        ...NFR_FIELDS.map(field => ({
            questionid: nfrQuestionIds[field.key],
            answer: edited.nfr[field.key]
        }))
    ];
}
